<template>
  <Badge
    :severity="badgeContent.type"
    class="border-round-3xl select-none"
    :class="badgeContent.clickable ? 'cursor-pointer' : ''"
    :value="badgeContent.label"
    @click="handleBadgeClick"
  />
  <AdstxtMissingLinesModal
    :visible="showAdstxtVerificationResult"
    :missing-lines="data.adstxt_missing_lines"
    :last-checked="data.adstxt_last_check"
    :ads-txt-per-demand="data.ads_txt_per_demand"
    @close-modal="showAdstxtVerificationResult = false"
  />
</template>
<script setup>
import { ref } from "vue";
import Badge from "primevue/badge";
import AdstxtMissingLinesModal from "./AdstxtMissingLinesModal.vue";
import { computed } from "vue";

const props = defineProps({
  data: Object,
});

const showAdstxtVerificationResult = ref(false);

const handleBadgeClick = () => {
  if (badgeContent.value.clickable) showAdstxtVerificationResult.value = true;
  return;
};

// Parse adstxt_missing_lines supporting both the legacy format (array of
// strings) and the structured format (array of {row, partner, type, priority})
// Stored JSON may contain raw control chars (Redshift escape quirk)
const parseMissingLinesJson = (raw) => {
  try {
    return JSON.parse(raw);
  } catch (e) {
    // eslint-disable-next-line no-control-regex
    return JSON.parse(raw.replace(/[\x00-\x1F]+/g, " "));
  }
};

const parsedMissingLines = computed(() => {
  const result = { lines: [], fetchFailed: false };
  if (!props.data?.adstxt_missing_lines) return result;
  try {
    const parsed = parseMissingLinesJson(props.data.adstxt_missing_lines);
    if (!Array.isArray(parsed)) return result;
    // audiopulsar.com is no longer a required line
    const isDropped = (row) =>
      String(row)
        .toLowerCase()
        .replace(/\s+/g, "")
        .startsWith("audiopulsar.com,");

    for (const item of parsed) {
      if (item && typeof item === "object" && item.row) {
        if (!isDropped(item.row)) result.lines.push(item);
      } else if (typeof item === "string") {
        if (
          item.includes("ads.txt file could not be fetched") ||
          item.includes("Error during validation")
        ) {
          result.fetchFailed = true;
        } else if (!isDropped(item)) {
          // Legacy strings carry no priority — treat as critical
          result.lines.push({ row: item, priority: 1 });
        }
      }
    }
  } catch (e) {
    console.error("Error parsing adstxt_missing_lines:", e);
  }
  return result;
});

const badgeContent = computed(() => {
  try {
    if (!props.data.adstxt_last_check)
      return { type: "warning", label: "Pending", clickable: false };

    if (parsedMissingLines.value.fetchFailed)
      return { type: "danger", label: "Missing", clickable: true };

    const missingTopTier = parsedMissingLines.value.lines.filter((line) => {
      if (line.mismatch) return false;
      // priority 0 (direct lines) is valid — `Number(x) || 3` would break it
      const priority = Number.isFinite(Number(line.priority))
        ? Number(line.priority)
        : 3;
      return priority <= 2;
    }).length;

    if (missingTopTier === 0)
      return { type: "success", label: "0", clickable: true };
    if (missingTopTier <= 4)
      return { type: "success", label: `${missingTopTier}`, clickable: true };
    return { type: "warning", label: `${missingTopTier}`, clickable: true };
  } catch (e) {
    console.error("Error computing ads.txt badge:", e);
    return { type: "warning", label: "Error", clickable: false };
  }
});
</script>
